import { api } from './api';

/**
 * The native shell, from the web app's point of view.
 *
 * One bundle serves three places: a browser tab, a Home Screen PWA, and the
 * App Store build. Everything that differs between them is behind this module,
 * so the screens never branch on which one they are running in.
 *
 * Every plugin is loaded dynamically and only after `isNative()` says there is
 * a shell to load it into. A browser tab therefore never downloads or runs any
 * of it, and a failure to load one plugin cannot take the app down with it —
 * the web behaviour is always the fallback.
 */

interface CapacitorGlobal {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
}

function capacitor(): CapacitorGlobal | undefined {
  return (window as unknown as { Capacitor?: CapacitorGlobal }).Capacitor;
}

/** True only inside the Capacitor shell — false in Safari and in the PWA. */
export function isNative(): boolean {
  return capacitor()?.isNativePlatform?.() === true;
}

export function platform(): string {
  return capacitor()?.getPlatform?.() ?? 'web';
}

// ── Push ────────────────────────────────────────────────────────────────────

/**
 * Registering for APNs is two asynchronous halves that must both be wired
 * before the first is started: `register()` only asks iOS to begin, and the
 * token arrives later on a listener. Attaching the listener after calling
 * register is a race that loses on a fast device and passes on a slow one.
 */
export async function registerForPush(): Promise<'on' | 'denied' | 'unsupported'> {
  if (!isNative()) return 'unsupported';

  const { PushNotifications } = await import('@capacitor/push-notifications');

  const existing = await PushNotifications.checkPermissions();
  let granted = existing.receive === 'granted';
  if (!granted) {
    const asked = await PushNotifications.requestPermissions();
    granted = asked.receive === 'granted';
  }
  if (!granted) return 'denied';

  const token = await new Promise<string | null>((resolve) => {
    // iOS can take a moment to reach APNs, and can silently never answer when
    // the device is offline. Without this the switch in Settings would sit
    // spinning for ever rather than saying it did not work.
    const timeout = setTimeout(() => resolve(null), 15_000);

    void PushNotifications.addListener('registration', (t) => {
      clearTimeout(timeout);
      resolve(t.value);
    });
    void PushNotifications.addListener('registrationError', () => {
      clearTimeout(timeout);
      resolve(null);
    });
    void PushNotifications.register();
  });

  if (!token) return 'unsupported';

  await api.post('/push/apns/register', {
    deviceToken: token,
    // A debug build's token is minted against Apple's sandbox and is rejected
    // by the production gateway with an error that reads like a bad token.
    environment: import.meta.env.DEV ? 'sandbox' : 'production',
  });
  return 'on';
}

export async function unregisterPush(): Promise<void> {
  if (!isNative()) return;
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    await PushNotifications.removeAllListeners();
  } catch { /* nothing registered */ }
}

/** Routes a tapped notification to the screen it is about. */
export async function onNotificationTap(handler: (url: string) => void): Promise<void> {
  if (!isNative()) return;
  const { PushNotifications } = await import('@capacitor/push-notifications');
  await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    const url = (action.notification.data as { url?: string } | undefined)?.url;
    if (url) handler(url);
  });
}

/**
 * Foreground/background transitions, from the shell rather than the document.
 *
 * `visibilitychange` fires in a WKWebView but `pagehide` largely does not, and
 * the app lock depends on knowing the moment the app leaves the screen. This
 * is the signal iOS itself sends, so it cannot be missed.
 *
 * Returns a function that removes the listener.
 */
export async function onAppStateChange(
  handler: (active: boolean) => void,
): Promise<() => void> {
  if (!isNative()) return () => {};
  try {
    const { App } = await import('@capacitor/app');
    const handle = await App.addListener('appStateChange', ({ isActive }) => handler(isActive));
    return () => { void handle.remove(); };
  } catch {
    return () => {};
  }
}

// ── Biometrics ──────────────────────────────────────────────────────────────

export interface BiometryStatus {
  available: boolean;
  /** 'faceId' | 'touchId' | 'none' — what to actually call it in the UI. */
  kind: 'faceId' | 'touchId' | 'none';
}

export async function biometryStatus(): Promise<BiometryStatus> {
  if (!isNative()) return { available: false, kind: 'none' };
  try {
    const { BiometricAuth, BiometryType } = await import('@aparajita/capacitor-biometric-auth');
    const result = await BiometricAuth.checkBiometry();
    const kind = result.biometryType === BiometryType.faceId ? 'faceId'
      : result.biometryType === BiometryType.touchId ? 'touchId'
        : 'none';
    return { available: result.isAvailable, kind };
  } catch {
    return { available: false, kind: 'none' };
  }
}

/**
 * The app-lock gate.
 *
 * Deliberately a local check rather than a passkey ceremony. The passkey path
 * signs a challenge and mints a new session, which is right for signing in and
 * far too heavy for returning to an app the user never signed out of — it
 * needs the network, so the lock would be unopenable on a plane.
 *
 * Returns false when the user cancels or fails; the caller keeps the app
 * locked and offers the password route, which is what stops the lock being a
 * trap on a phone whose Face ID has stopped recognising its owner.
 */
export async function biometricUnlock(reason: string): Promise<boolean> {
  if (!isNative()) return false;
  try {
    const { BiometricAuth } = await import('@aparajita/capacitor-biometric-auth');
    await BiometricAuth.authenticate({
      reason,
      cancelTitle: 'Cancel',
      // The device passcode is the honest fallback: it is what protects the
      // phone itself, and refusing it would lock out anyone whose face has
      // changed — glasses, a beard, a bad angle — with no way back in.
      allowDeviceCredential: true,
      iosFallbackTitle: 'Use passcode',
    });
    return true;
  } catch {
    return false;
  }
}

// ── Widget ──────────────────────────────────────────────────────────────────

export interface WidgetSnapshot {
  spentTodayMinor: number;
  spentMonthMinor: number;
  budgetMonthMinor: number | null;
  currency: string;
  updatedAt: string;
}

interface FintrackBridgePlugin {
  setWidgetSnapshot(options: { json: string }): Promise<void>;
  clearWidgetSnapshot(): Promise<void>;
  setIngestConfig(options: { apiBase: string; ingestKey: string }): Promise<void>;
  clearIngestConfig(): Promise<void>;
}

/**
 * The App Group bridge.
 *
 * `@capacitor/preferences` looks like the obvious way to do this and is not:
 * its `group` option is only a key prefix inside `UserDefaults.standard`, so
 * the widget — a separate process with its own container — would never see a
 * thing. Reaching the shared suite needs native code, which is what
 * FintrackBridge.swift is.
 */
let bridge: FintrackBridgePlugin | null = null;

async function nativeBridge(): Promise<FintrackBridgePlugin | null> {
  if (!isNative()) return null;
  if (bridge) return bridge;
  const { registerPlugin } = await import('@capacitor/core');
  bridge = registerPlugin<FintrackBridgePlugin>('FintrackBridge');
  return bridge;
}

/**
 * Hands the Home Screen widget today's figures.
 *
 * The widget is a separate process and cannot call the API — it has no session
 * and no way to get one. So the app writes what it already fetched into the
 * shared App Group container, and the widget renders whatever was last written.
 * That makes the widget exactly as fresh as the last time the app was opened,
 * which is the honest trade for not shipping credentials into a second process.
 */
export async function writeWidgetSnapshot(snapshot: WidgetSnapshot): Promise<void> {
  try {
    const b = await nativeBridge();
    await b?.setWidgetSnapshot({ json: JSON.stringify(snapshot) });
  } catch { /* the widget simply keeps its previous figures */ }
}

/**
 * Gives the Shortcuts intent what it needs to post a message on its own.
 *
 * The intent runs without the app, so it cannot ask the app for anything. The
 * ingest key goes into the shared keychain once, here, instead of the user
 * copying it into a Shortcut by hand — which is the step that has gone wrong
 * for every person who has set this up so far.
 */
export async function shareIngestConfig(apiBase: string, ingestKey: string): Promise<void> {
  try {
    const b = await nativeBridge();
    await b?.setIngestConfig({ apiBase, ingestKey });
  } catch { /* the URL-based Shortcut still works */ }
}

export async function clearIngestConfig(): Promise<void> {
  try {
    const b = await nativeBridge();
    await b?.clearIngestConfig();
  } catch { /* nothing stored */ }
}

/**
 * Everything this app left outside its own process, removed.
 *
 * Called on every sign-out, including the involuntary ones — an expired
 * session, a revoked token, a deleted account. Both things it clears live
 * beyond the app's own lock: the widget draws the last figures onto the Home
 * Screen where anyone can read them without unlocking anything, and the ingest
 * key would go on filing the next person's bank messages into an account they
 * no longer have.
 */
export async function clearNativeState(): Promise<void> {
  if (!isNative()) return;
  try {
    const b = await nativeBridge();
    // Sequential rather than Promise.all: if the first throws, the second must
    // still run. A stale widget is the more visible failure, the stale key the
    // more consequential one, and neither should be able to strand the other.
    await b?.clearWidgetSnapshot().catch(() => {});
    await b?.clearIngestConfig().catch(() => {});
  } catch { /* not in a shell, or the bridge is unavailable */ }
}
